import { Link } from "react-router-dom";
import { KindBadge, ProgressBar } from "@/components/new-look/blocks";
import { STAGES, type Stage } from "@/components/new-look/labels";
import { NEEDS_YOU_TEXT, stageOf } from "@/components/new-look/stage";
import { useEpicRequests } from "@/hooks/queries/use-epic-requests";
import type { EpicRequest, EpicRequestFilters } from "@/lib/api/epic-requests";
import { cn } from "@/lib/utils";
import { factsFor, progressOf, updatedAgo } from "./idea-helpers";

/**
 * All ideas as a board (docs/new-look/PLAN.md, step 2.5): a column per stage,
 * each asking the list for its own stage so every column is complete.
 */

const COLUMN_SIZE = 20;

const DOT: Record<Stage, string> = {
  idea: "bg-violet-600 dark:bg-violet-400",
  blueprint: "bg-cyan-600 dark:bg-cyan-400",
  build: "bg-blue-600 dark:bg-blue-400",
  done: "bg-green-600 dark:bg-green-400",
};

function BoardCard({ idea }: { idea: EpicRequest }) {
  const { stage, needsYou } = stageOf(factsFor(idea));
  const progress = progressOf(idea, stage);
  return (
    <li>
      <Link
        to={`/ideas/${idea.id}`}
        className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3 transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-start gap-2">
          <KindBadge kind="software" className="h-6 w-6" />
          <span className="min-w-0 text-sm font-semibold leading-snug text-foreground">
            {idea.title}
          </span>
        </span>
        {needsYou && (
          <span className="self-start rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
            {NEEDS_YOU_TEXT[needsYou]}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {[idea.team?.name ?? "Personal", updatedAgo(idea.updatedAt)].filter(Boolean).join(" · ")}
        </span>
        {progress !== null && (
          <ProgressBar value={progress} label={`${idea.title} progress`} stage={stage} />
        )}
      </Link>
    </li>
  );
}

function BoardColumn({ stage, filters }: { stage: Stage; filters: EpicRequestFilters }) {
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useEpicRequests({
      ...filters,
      limit: COLUMN_SIZE,
      stages: stage,
    });
  const ideas = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.meta.totalCount;

  return (
    <section
      aria-label={STAGES[stage]}
      className="flex w-[78vw] max-w-[320px] shrink-0 snap-start flex-col gap-2 rounded-2xl border border-border/60 bg-muted/40 p-2.5 md:w-auto md:max-w-none md:shrink"
    >
      <h2 className="flex items-center justify-between px-1.5 py-1 text-[15px] font-semibold text-foreground">
        <span className="flex items-center gap-2">
          <span aria-hidden="true" className={cn("h-2 w-2 rounded-full", DOT[stage])} />
          {STAGES[stage]}
        </span>
        {total !== undefined && (
          <span className="text-sm tabular-nums text-muted-foreground">{total}</span>
        )}
      </h2>
      {isLoading ? (
        <div
          aria-busy="true"
          aria-label={`Loading ${STAGES[stage]}`}
          className="flex flex-col gap-2"
        >
          <div className="h-20 animate-pulse rounded-xl bg-muted" />
          <div className="h-20 animate-pulse rounded-xl bg-muted" />
        </div>
      ) : isError ? (
        <p className="px-1.5 py-4 text-sm text-muted-foreground">Couldn't load this column.</p>
      ) : ideas.length === 0 ? (
        <p className="px-1.5 py-4 text-sm text-muted-foreground">Nothing here.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ideas.map((idea) => (
            <BoardCard key={idea.id} idea={idea} />
          ))}
        </ul>
      )}
      {hasNextPage && (
        <button
          type="button"
          disabled={isFetchingNextPage}
          onClick={() => {
            void fetchNextPage();
          }}
          className="min-h-11 rounded-lg text-sm font-medium text-primary hover:bg-background disabled:opacity-60"
        >
          {isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}
    </section>
  );
}

/**
 * @param filters the list's filters without a stage
 * @param stages the columns to show (a view like Building now shows one)
 */
export function IdeasBoard({
  filters,
  stages,
}: {
  filters: EpicRequestFilters;
  stages: readonly Stage[];
}) {
  return (
    <div
      className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:snap-none md:overflow-visible md:px-0"
      style={{ gridTemplateColumns: `repeat(${String(stages.length)}, minmax(0, 1fr))` }}
    >
      {stages.map((stage) => (
        <BoardColumn key={stage} stage={stage} filters={filters} />
      ))}
    </div>
  );
}
